import { Link } from 'react-router';
import { DEFAULT_ANIMATION, parseAnimation, type PreviewTemplate } from '@game-platform/pack-animation';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import type { PoolOfferConfig } from '../../lib/api/operatorConsole';
import { AnimationEditorPanel } from './AnimationEditorPanel';
import { packTitle } from './animationScope';
import { PackOpeningNotice } from './PackOpeningNotice';
import { PackOpeningPreview } from './PackOpeningPreview';
import { useAnimationEditor } from './useAnimationEditor';
import type { GlobalAnimation } from './useGlobalAnimation';
import { usePackArtwork } from './usePackArtwork';
import { usePackOpeningWrite } from './usePackOpeningWrite';

// Without themes access the preview still plays, on the template's stock look
const STOCK_THEME = { version: 2 };
const DISCARD_QUESTION = 'Discard unsaved animation changes?';

interface Props {
  pack: PoolOfferConfig;
  template: PreviewTemplate;
  /** What packs without their own opening play; null when it could not be read. */
  global: GlobalAnimation | null;
  /** The Pack Opening page, where the global animation is set, is open to this user. */
  canEditGlobal: boolean;
  onClose: () => void;
  onSaved: (updated: PoolOfferConfig, message: string) => void;
}

/** One pack's opening animation with a live preview. Saves to this pack only, never globally. */
export function PackOpeningModal({ pack, template, global, canEditGlobal, onClose, onSaved }: Props) {
  const custom = pack.animation != null;
  const [state, dispatch] = useAnimationEditor(
    pack.animation ? parseAnimation(pack.animation) : (global?.animation ?? DEFAULT_ANIMATION),
  );
  const { busy, error, write } = usePackOpeningWrite(pack.pool_offer_id);
  const { artwork, reload: reloadArtwork } = usePackArtwork(pack);
  const title = packTitle(pack);
  // With the global animation unknown the editor's starting mechanic is a pick, not what the pack plays
  const canSave = !busy && (state.dirty || (!custom && !global));

  const confirmLeave = () => !state.dirty || window.confirm(DISCARD_QUESTION);

  const close = () => {
    if (!busy && confirmLeave()) onClose();
  };

  const save = async () => {
    const updated = await write(state.animation);
    if (updated) onSaved(updated, `Opening animation saved for ${title}`);
  };

  const revertToGlobal = async () => {
    const lost = state.dirty ? ' Unsaved changes will be lost.' : '';
    if (!window.confirm(`${title} will play the global animation again.${lost}`)) return;
    const updated = await write(null);
    if (updated) onSaved(updated, `${title} uses the global animation`);
  };

  const footer = (
    <div className="flex flex-wrap items-center gap-3">
      {canEditGlobal && (
        <Link
          to="/animation"
          aria-disabled={busy}
          onClick={(e) => {
            // Leaving would not stop a write already sent
            if (busy || !confirmLeave()) e.preventDefault();
          }}
          className="text-[12px] text-[var(--co-muted)] underline-offset-2 hover:text-[var(--co-text)] hover:underline"
        >
          Change the animation for all packs →
        </Link>
      )}
      {error && (
        <p role="alert" className="text-[12px] text-[var(--co-error)]">
          {error}
        </p>
      )}
      <div className="ml-auto flex items-center gap-2">
        {custom && (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => void revertToGlobal()}
            className="px-3 py-1.5 text-[12px]"
          >
            Use global animation
          </Button>
        )}
        <Button variant="ghost" disabled={busy} onClick={close} className="px-3 py-1.5 text-[12px]">
          Cancel
        </Button>
        <Button
          disabled={!canSave}
          onClick={() => void save()}
          className="px-3 py-1.5 text-[12px]"
        >
          {busy ? 'Saving…' : 'Save for this pack'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal open title={`Opening animation: #${pack.pool_offer_id} ${title}`} onClose={close} size="full" footer={footer}>
      <div className="flex min-h-0 flex-1 flex-col">
        <PackOpeningNotice custom={custom} global={global} />
        {/* Below this height the modal body scrolls instead of crushing the preview */}
        <div className="flex min-h-[420px] flex-1">
          <fieldset disabled={busy} className="contents">
            <AnimationEditorPanel
              animation={state.animation}
              template={template}
              dispatch={dispatch}
              onVariantChange={() => void reloadArtwork()}
            />
          </fieldset>
          <PackOpeningPreview
            animation={state.animation}
            designSystem={global?.designSystem ?? STOCK_THEME}
            template={template}
            artwork={artwork}
            locked={busy}
            onReloadArtwork={() => void reloadArtwork()}
            onResetOptions={() => dispatch({ type: 'reset-options' })}
          />
        </div>
      </div>
    </Modal>
  );
}
